import React, { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../Component/Layout";
import { FaPlus, FaEdit, FaTrash, FaTimes } from "react-icons/fa";
import { MASTER_API } from "../apiConfig";

const emptyForm = {
    location_label: "",
    company_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    pin_code: "",
    state: "",
    country: "",
    tel_no: "",
    gst_no: "",
    cin_no: "",
    iec_no: "",
    phone1: "",
    phone2: "",
    phone3: "",
    email1: "",
    email2: ""
};

const LocationMaster = () => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formTab, setFormTab] = useState("basic");
    const [form, setForm] = useState(emptyForm);

    const [confirmBox, setConfirmBox] = useState({ show: false, action: null, id: null });
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3000);
    };

    // ─── FETCH ───────────────────────────────────────────────
    const fetchLocations = async (search = "") => {
        setLoading(true);
        try {
            const res = await axios.get(`${MASTER_API}/location-master`, {
                params: { search }
            });
            setLocations(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Failed to load locations", err);
            setLocations([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLocations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const delay = setTimeout(() => {
            fetchLocations(searchTerm);
        }, 300);
        return () => clearTimeout(delay);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    // ─── MODAL HELPERS ───────────────────────────────────────
    const openAddModal = () => {
        setEditingId(null);
        setForm(emptyForm);
        setFormTab("basic");
        setShowModal(true);
    };

    const openEditModal = (loc) => {
        setEditingId(loc.id);
        setForm({
            location_label: loc.location_label || "",
            company_name: loc.company_name || "",
            address_line1: loc.address_line1 || "",
            address_line2: loc.address_line2 || "",
            city: loc.city || "",
            pin_code: loc.pin_code || "",
            state: loc.state || "",
            country: loc.country || "",
            tel_no: loc.tel_no || "",
            gst_no: loc.gst_no || "",
            cin_no: loc.cin_no || "",
            iec_no: loc.iec_no || "",
            phone1: loc.phone1 || "",
            phone2: loc.phone2 || "",
            phone3: loc.phone3 || "",
            email1: loc.email1 || "",
            email2: loc.email2 || ""
        });
        setFormTab("basic");
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(emptyForm);
        setFormTab("basic");
    };

    const handleChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    // ─── SAVE (ADD / UPDATE) ─────────────────────────────────
    const handleSaveClick = () => {
        setConfirmBox({ show: true, action: editingId ? "update" : "add", id: null });
    };

    const handleConfirmSave = async () => {
        try {
            if (editingId) {
                await axios.put(`${MASTER_API}/location-master/${editingId}`, form);
                showToast("Updated successfully");
            } else {
                await axios.post(`${MASTER_API}/location-master`, form);
                showToast("Location added successfully");
            }
            setConfirmBox({ show: false, action: null, id: null });
            closeModal();
            fetchLocations(searchTerm);
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.error ||
                (editingId ? "Update failed" : "Insert failed");
            showToast(message, "error");
            setConfirmBox({ show: false, action: null, id: null });
        }
    };

    // ─── DELETE ──────────────────────────────────────────────
    const handleDeleteClick = (id) => {
        setConfirmBox({ show: true, action: "delete", id });
    };

    const handleConfirmDelete = async () => {
        try {
            await axios.delete(`${MASTER_API}/location-master/${confirmBox.id}`);
            showToast("Deleted successfully");
            fetchLocations(searchTerm);
        } catch (err) {
            console.error(err);
            showToast("Delete failed", "error");
        } finally {
            setConfirmBox({ show: false, action: null, id: null });
        }
    };

    const handleConfirm = () => {
        if (confirmBox.action === "delete") return handleConfirmDelete();
        return handleConfirmSave();
    };

    return (
        <Layout>
            <div style={{ padding: "30px 40px", backgroundColor: "#f8fafc", minHeight: "calc(100vh - 60px)" }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "24px",
                        flexWrap: "wrap",
                        gap: "12px"
                    }}
                >
                    <div>
                        <h2 style={{ margin: "0 0 8px 0", color: "#0f172a", fontWeight: 700, fontSize: "26px" }}>
                            Location Master
                        </h2>
                        <p style={{ color: "#64748b", margin: 0, fontSize: "14px" }}>
                            Manage all company locations and addresses.
                        </p>
                    </div>

                    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                        <input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search company, city, state, pin code..."
                            style={{
                                padding: "10px 14px",
                                borderRadius: "10px",
                                border: "1px solid #cbd5e1",
                                fontSize: "14px",
                                minWidth: "260px",
                                outline: "none"
                            }}
                        />

                        <button onClick={openAddModal} style={addBtnStyle}>
                            <FaPlus size={13} /> Add Location
                        </button>
                    </div>
                </div>

                <div
                    style={{
                        background: "#fff",
                        borderRadius: "16px",
                        border: "1px solid #e2e8f0",
                        overflow: "auto"
                    }}
                >
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1400px" }}>
                        <thead>
                            <tr style={{ background: "#f1f5f9", textAlign: "left" }}>
                                <th style={thStyle}>Label</th>
                                <th style={thStyle}>Company Name</th>
                                <th style={thStyle}>Address Line 1</th>
                                <th style={thStyle}>Address Line 2</th>
                                <th style={thStyle}>City</th>
                                <th style={thStyle}>Pin Code</th>
                                <th style={thStyle}>State</th>
                                <th style={thStyle}>Country</th>
                                <th style={thStyle}>Tel No.</th>
                                <th style={thStyle}>GST No.</th>
                                <th style={thStyle}>CIN No.</th>
                                <th style={thStyle}>IEC No.</th>
                                <th style={thStyle}>Phone 1</th>
                                <th style={thStyle}>Phone 2</th>
                                <th style={thStyle}>Phone 3</th>
                                <th style={thStyle}>Email 1</th>
                                <th style={thStyle}>Email 2</th>
                                <th style={{ ...thStyle, textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={18} style={{ padding: "24px", textAlign: "center", color: "#94a3b8" }}>
                                        Loading...
                                    </td>
                                </tr>
                            ) : locations.length === 0 ? (
                                <tr>
                                    <td colSpan={18} style={{ padding: "24px", textAlign: "center", color: "#94a3b8" }}>
                                        No locations found. Click "Add Location" to create one.
                                    </td>
                                </tr>
                            ) : (
                                locations.map((loc, i) => (
                                    <tr key={loc.id} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderTop: "1px solid #eef2f6" }}>
                                        <td style={tdStyle}>{loc.location_label || "-"}</td>
                                        <td style={tdStyle}>{loc.company_name}</td>
                                        <td style={tdStyle}>{loc.address_line1}</td>
                                        <td style={tdStyle}>{loc.address_line2 || "-"}</td>
                                        <td style={tdStyle}>{loc.city}</td>
                                        <td style={tdStyle}>{loc.pin_code}</td>
                                        <td style={tdStyle}>{loc.state}</td>
                                        <td style={tdStyle}>{loc.country}</td>
                                        <td style={tdStyle}>{loc.tel_no || "-"}</td>
                                        <td style={tdStyle}>{loc.gst_no || "-"}</td>
                                        <td style={tdStyle}>{loc.cin_no || "-"}</td>
                                        <td style={tdStyle}>{loc.iec_no || "-"}</td>
                                        <td style={tdStyle}>{loc.phone1 || "-"}</td>
                                        <td style={tdStyle}>{loc.phone2 || "-"}</td>
                                        <td style={tdStyle}>{loc.phone3 || "-"}</td>
                                        <td style={tdStyle}>{loc.email1 || "-"}</td>
                                        <td style={tdStyle}>{loc.email2 || "-"}</td>
                                        <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                                                <button onClick={() => openEditModal(loc)} style={editBtnStyle}>
                                                    <FaEdit size={12} /> Edit
                                                </button>
                                                <button onClick={() => handleDeleteClick(loc.id)} style={deleteBtnStyle}>
                                                    <FaTrash size={13} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ─── ADD / EDIT MODAL ─────────────────────────── */}
                {showModal && (
                    <div style={overlayStyle}>
                        <div style={modalStyle}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                                <h3 style={{ margin: 0, fontSize: "18px", color: "#0f172a" }}>
                                    {editingId ? "Edit Location" : "Add Location"}
                                </h3>
                                <FaTimes style={{ cursor: "pointer" }} onClick={closeModal} />
                            </div>

                            {/* TABS */}
                            <div style={{ display: "flex", gap: "6px", marginBottom: "18px", borderBottom: "1px solid #e2e8f0" }}>
                                <button
                                    type="button"
                                    onClick={() => setFormTab("basic")}
                                    style={formTab === "basic" ? tabActiveStyle : tabInactiveStyle}
                                >
                                    Basic Info
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormTab("address")}
                                    style={formTab === "address" ? tabActiveStyle : tabInactiveStyle}
                                >
                                    Address
                                </button>
                            </div>

                            {/* BASIC INFO TAB */}
                            {formTab === "basic" && (
                                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                    <input value={form.tel_no} onChange={handleChange("tel_no")} placeholder="Tel No." style={inputStyle} />
                                    <input value={form.gst_no} onChange={handleChange("gst_no")} placeholder="GST No." style={inputStyle} />
                                    <input value={form.cin_no} onChange={handleChange("cin_no")} placeholder="CIN No." style={inputStyle} />
                                    <input value={form.iec_no} onChange={handleChange("iec_no")} placeholder="IEC No." style={inputStyle} />
                                    <input value={form.phone1} onChange={handleChange("phone1")} placeholder="Phone 1" style={inputStyle} />
                                    <input value={form.phone2} onChange={handleChange("phone2")} placeholder="Phone 2" style={inputStyle} />
                                    <input value={form.phone3} onChange={handleChange("phone3")} placeholder="Phone 3" style={inputStyle} />
                                    <input type="email" value={form.email1} onChange={handleChange("email1")} placeholder="Email 1" style={inputStyle} />
                                    <input type="email" value={form.email2} onChange={handleChange("email2")} placeholder="Email 2" style={inputStyle} />
                                </div>
                            )}

                            {/* ADDRESS TAB */}
                            {formTab === "address" && (
                                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                    <input value={form.location_label} onChange={handleChange("location_label")} placeholder="Label (e.g. Unit 1, Unit 2)" style={inputStyle} />
                                    <input value={form.company_name} onChange={handleChange("company_name")} placeholder="Company Name" style={inputStyle} />
                                    <input value={form.address_line1} onChange={handleChange("address_line1")} placeholder="Address Line 1" style={inputStyle} />
                                    <input value={form.address_line2} onChange={handleChange("address_line2")} placeholder="Address Line 2" style={inputStyle} />
                                    <input value={form.city} onChange={handleChange("city")} placeholder="City" style={inputStyle} />
                                    <input value={form.pin_code} onChange={handleChange("pin_code")} placeholder="Pin Code" style={inputStyle} />
                                    <input value={form.state} onChange={handleChange("state")} placeholder="State" style={inputStyle} />
                                    <input value={form.country} onChange={handleChange("country")} placeholder="Country" style={inputStyle} />
                                </div>
                            )}

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "22px" }}>
                                <button onClick={closeModal} style={cancelBtnStyle}>Cancel</button>
                                <button onClick={handleSaveClick} style={saveBtnStyle}>Save</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ─── CONFIRM DIALOG ───────────────────────────── */}
                {confirmBox.show && (
                    <div style={overlayStyle}>
                        <div style={{ ...modalStyle, width: "360px" }}>
                            <p style={{ margin: "0 0 20px 0", fontSize: "15px", color: "#1e293b" }}>
                                {confirmBox.action === "delete"
                                    ? "Are you sure you want to delete this location?"
                                    : confirmBox.action === "update"
                                    ? "Save changes to this location?"
                                    : "Add this new location?"}
                            </p>
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                                <button
                                    onClick={() => setConfirmBox({ show: false, action: null, id: null })}
                                    style={cancelBtnStyle}
                                >
                                    Cancel
                                </button>
                                <button onClick={handleConfirm} style={saveBtnStyle}>
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ─── TOAST ─────────────────────────────────────── */}
                {toast.show && (
                    <div
                        style={{
                            position: "fixed",
                            bottom: "24px",
                            right: "24px",
                            background: toast.type === "error" ? "#dc2626" : "#16a34a",
                            color: "#fff",
                            padding: "12px 20px",
                            borderRadius: "10px",
                            fontSize: "14px",
                            fontWeight: 600,
                            boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                            zIndex: 3000
                        }}
                    >
                        {toast.message}
                    </div>
                )}
            </div>
        </Layout>
    );
};

const addBtnStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#021843",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "10px 18px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer"
};

const thStyle = { padding: "12px 16px", fontSize: "13px", color: "#475569", fontWeight: 600, whiteSpace: "nowrap" };
const tdStyle = { padding: "12px 16px", fontSize: "14px", color: "#1e293b", whiteSpace: "nowrap" };
const editBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#fff",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    padding: "6px 12px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#1e293b",
    cursor: "pointer"
};

const deleteBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
    border: "1px solid #fecaca",
    borderRadius: "8px",
    padding: "6px 10px",
    color: "#ef4444",
    cursor: "pointer"
};

const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000
};

const modalStyle = {
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    width: "460px",
    maxWidth: "90vw",
    maxHeight: "85vh",
    overflowY: "auto"
};

const inputStyle = {
    padding: "9px 12px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box"
};

const cancelBtnStyle = {
    padding: "9px 16px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#fff",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 600,
    color: "#475569"
};

const saveBtnStyle = {
    padding: "9px 18px",
    borderRadius: "8px",
    border: "none",
    background: "#021843",
    color: "#fff",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 600
};

const tabActiveStyle = {
    padding: "8px 18px",
    border: "none",
    background: "#021843",
    color: "#fff",
    borderRadius: "6px 6px 0 0",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600
};

const tabInactiveStyle = {
    padding: "8px 18px",
    border: "1px solid #e2e8f0",
    borderBottom: "none",
    background: "#f8fafc",
    color: "#334155",
    borderRadius: "6px 6px 0 0",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 500
};

export default LocationMaster;